import { z } from "zod";
/* Integer Types */
export const zSmallint = (min = -32768, max = 32767) => z.int().min(min).max(max);
export const zInteger = (min = -2147483648, max = 2147483647) => z.int32().min(min).max(max);
export const zBigint = (min = -9223372036854775808n, max = 9223372036854775807n) => z.bigint().min(min).max(max);
export const zTimeEpoch = z.coerce.number().int().min(0);
/**
 * Regex-based decimal validator. The previous toString()-based version
 * mis-counted significant digits for scientific notation (e.g. `1e10`) and
 * mishandled leading zeros. This version normalises the input to a canonical
 * string then matches against a precision-aware regex.
 */
export const zDecimal = (precision, scale, min, max) => {
    const maxStrLen = 1 + Math.max(1, precision - scale) + (scale > 0 ? 1 : 0) + scale;
    const intDigits = precision - scale;
    const integerRegexPart = intDigits > 0 ? `0*\\d{1,${intDigits}}` : `0+`;
    const decimalPart = scale > 0 ? `(\\.\\d{1,${scale}})?` : "";
    const signPart = min !== undefined && min >= 0 ? "" : "-?";
    const regex = new RegExp(`^${signPart}${integerRegexPart}${decimalPart}$`);
    const regexMessage = `Value must be a valid decimal with at most ${precision - scale} integer digits and ${scale} decimal places`;
    let schema = z.preprocess((val) => {
        if (val === null || val === undefined)
            return val;
        const str = typeof val === "string" ? val : String(val);
        return str.trim().replace(/^(-?)0+(?=\d)/, "$1");
    }, z.string().min(1).max(maxStrLen).regex(regex, regexMessage));
    if (min !== undefined) {
        schema = schema.refine((val) => {
            const num = Number(val);
            return !Number.isNaN(num) && num >= min;
        }, { message: `Value must be greater than or equal to ${min}` });
    }
    if (max !== undefined) {
        schema = schema.refine((val) => {
            const num = Number(val);
            return !Number.isNaN(num) && num <= max;
        }, { message: `Value must be less than or equal to ${max}` });
    }
    return schema;
};
/* Common Types */
export const zString = z.string().trim();
export const zVarchar = (minLength = 0, maxLength = 255) => zString.min(minLength).max(maxLength);
export const zText = (minLength = 0) => zString.min(minLength);
/**
 * Default timestamps shape for SYNCED tables. `updatedAt` is a µs-string
 * because the pull-delta protocol needs microsecond precision + strict
 * ordering.
 */
export const zTimestamps = {
    createdAt: zTimeEpoch,
    updatedAt: zTimeEpoch,
};
// Decimal helpers — zero is allowed (B2B inventory may legitimately have
// zero-value items, free promo prices, etc.). Tighten per-call via the min arg
// if a stricter floor is needed for a specific field.
export const zPercent = zDecimal(5, 2, 0, 100.0);
export const zPrice = zDecimal(10, 2, 0);
export const zQuantity = zDecimal(11, 3, 0);
export const zAmount = (min = Number.NEGATIVE_INFINITY, max = Number.POSITIVE_INFINITY) => zDecimal(15, 2, min, max);
export const zAmountNonNegative = zAmount(0);
/* Compliance Doc Types */
export const zGSTIN = zString
    .toUpperCase()
    .length(15)
    .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, "Invalid GSTIN format")
    .refine((gstin) => {
    const GSTIN_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const chars = gstin.slice(0, 14);
    const len = GSTIN_CHARS.length;
    const total = chars.split("").reduce((acc, char, i) => {
        const codePoint = GSTIN_CHARS.indexOf(char);
        const weight = i % 2 === 0 ? 1 : 2;
        const product = codePoint * weight;
        return acc + Math.floor(product / len) + (product % len);
    }, 0);
    const checksumCodePoint = (len - (total % len)) % len;
    return gstin[14] === GSTIN_CHARS[checksumCodePoint];
}, "Invalid GSTIN checksum");
export const zPAN = zString
    .toUpperCase()
    .length(10)
    .regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/);
export const zUdyogAadhaar = zString
    .toUpperCase()
    .length(12)
    .regex(/^[2-9]{1}[0-9]{3}\s[0-9]{4}\s[0-9]{4}$/);
export const zUdyamRegistrationNumber = zString
    .toUpperCase()
    .length(19)
    .regex(/^UDYAM-[A-Z]{2}-[0]{2}-\d{7}$/);
/* Contact Types */
/**
 * The length is counted in **digits**, which is what the message has always
 * said and what E.164 actually bounds (15, plus a country code that cannot
 * start at zero).
 *
 * Counting characters instead made the limit depend on how the number was
 * typed: `+91 962 470 5678` is a twelve-digit number and sixteen characters,
 * so it failed a rule announcing a fifteen-DIGIT maximum — on a form whose own
 * placeholder is spaced and whose helper text promises spaces are normalised
 * server-side. Formatting is still restricted to spaces, hyphens and brackets;
 * it just no longer counts against the number.
 */
const phoneDigits = (value) => value.replace(/\D/g, "");
export const zPhoneNumber = z
    .string()
    .trim()
    .regex(/^[+]?[1-9][\d\s\-()]*$/, "Invalid phone number format")
    .refine((value) => phoneDigits(value).length >= 10, "Phone number must be at least 10 digits")
    .refine((value) => phoneDigits(value).length <= 15, "Phone number must be at most 15 digits");
/* Location Types */
export const zLatitude = zString.regex(/^(\+|-)?(?:90(?:(?:\.0{1,6})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,6})?))$/);
export const zLongitude = zString.regex(/^(\+|-)?(?:180(?:(?:\.0{1,6})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,6})?))$/);
export const zTimezone = zString.describe("IANA time zone name");
/**
 * A list of distinct times of day, as `HH:mm`.
 *
 * **Seconds are stripped on the way in rather than rejected**, and that is a
 * fix rather than a leniency. `users.digestTimes` is a Postgres `time[]`, so
 * every read hands back `"08:00:00"` for a value this contract calls
 * `"08:00"` — the column declares an inner `.parse()` to strip them but
 * orchid does not fan an inner parse across array elements. Readers had each
 * grown their own `slice(0, 5)` at the API boundary; the one that had not was
 * the session transform feeding the browser's auth cache, where the resulting
 * ZodError was caught, logged and swallowed, so the cache silently stopped
 * being written and offline fallback stopped working with nothing on screen
 * saying so.
 *
 * Normalising here is what makes the next reader impossible to get wrong. It
 * widens the INPUT side too — `setDigestTimes` now accepts `"08:00:00"` — and
 * that is harmless: uniqueness is checked after normalisation, so `"08:00"`
 * and `"08:00:00"` correctly collide instead of both being stored.
 */
export const uniqueTimeArrayZod = z
    .preprocess((value) => Array.isArray(value)
    ? value.map((time) => typeof time === "string" && time.length > 5 ? time.slice(0, 5) : time)
    : value, z.array(z.iso.time({ precision: -1 })))
    .refine((times) => new Set(times).size === times.length, {
    message: "Times must be unique",
})
    .default([]);
/**
 * Normalises query-string params that may arrive as a single string OR an
 * array of strings into `string[]`. Use for repeated query keys
 * (e.g. `?tag=a&tag=b`) — without this, Zod refuses the single-value form.
 */
export const zStringArrayQuery = z.preprocess((val) => {
    if (val === undefined || val === null)
        return undefined;
    if (Array.isArray(val))
        return val;
    return [val];
}, z.array(z.string()));
//# sourceMappingURL=zod_utils.js.map